import { NavLink, useLocation } from "react-router-dom";
import clsx from "clsx";
import { ChevronLeft } from "lucide-react";
import {
  getActivePrimaryId,
  getPrimarySection,
} from "../../nav/primary-nav.js";
import { useAuth } from "../../context/AuthContext.js";
import { useSidebarCollapse } from "./AppLayout.js";
import {
  useTenantFeatureFlags,
  useTenantBetFlags,
} from "../../lib/tenant-feature-flags.js";

export const SECONDARY_NAV_WIDTH = 220;

export function SecondaryNav() {
  const location = useLocation();
  const { user } = useAuth();
  const { toggle } = useSidebarCollapse();
  const { flags, loading: flagsLoading } = useTenantFeatureFlags({
    role: user?.role ?? null,
    tenantExternalId: user?.tenantExternalId ?? null,
  });
  const { flags: betFlags, loading: betFlagsLoading } = useTenantBetFlags({
    role: user?.role ?? null,
    tenantExternalId: user?.tenantExternalId ?? null,
  });
  const activePrimaryId = getActivePrimaryId(location.pathname, location.search);
  const section = getPrimarySection(activePrimaryId);

  if (!section) return null;

  // Feature-gated links (e.g. Pilot success) only appear once the tenant has
  // the flag granted. While flags load we hide gated links to avoid a flash.
  const visibleLinks = section.links.filter((link) => {
    if (link.featureKey) {
      if (flagsLoading) return false;
      if (flags[link.featureKey] !== true) return false;
    }
    // Sprint S6: experimental-bet gate. Hide the link while bet flags load or
    // when the bet is not enabled for this tenant.
    if (link.betKey) {
      if (betFlagsLoading) return false;
      if (betFlags[link.betKey] !== true) return false;
    }
    return true;
  });

  return (
    <aside
      className="fixed top-0 bottom-0 z-40 bg-card border-r border-card overflow-y-auto"
      style={{ left: 80, width: SECONDARY_NAV_WIDTH }}
    >
      {/* Collapse toggle — collapses the secondary nav independently */}
      <button
        type="button"
        onClick={toggle}
        title="Collapse navigation"
        aria-label="Collapse navigation"
        className="absolute right-2 top-2 rounded-md p-1 text-subtle hover:bg-app-hover hover:text-app transition-colors duration-150"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>

      {/* Section Title — colored with the section's accent */}
      <div className="px-5 pt-5 pb-3">
        <h2 className="text-base font-semibold" style={{ color: section.accent ?? "var(--text-app)" }}>
          {section.label}
        </h2>
      </div>

      {/* Links */}
      <nav className="px-3 pb-4" aria-label="Secondary navigation">
        <ul className="space-y-0.5">
          {visibleLinks.map((link, i) => {
            // Render a small sub-group header when the groupLabel starts a new
            // group (and there is more than one group in this section).
            const prev = visibleLinks[i - 1];
            const showHeader =
              !!link.groupLabel &&
              link.groupLabel !== prev?.groupLabel &&
              visibleLinks.some((l) => l.groupLabel && l.groupLabel !== link.groupLabel);
            return (
              <li key={link.id}>
                {showHeader ? (
                  <p className="px-3 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-widest text-subtle">
                    {link.groupLabel}
                  </p>
                ) : null}
                <NavLink
                  to={link.to}
                  end={link.end}
                  className={({ isActive }) =>
                    clsx(
                      // Active gets a left glow bar via ::before (Sprint 2).
                      "relative block rounded-md text-sm font-medium transition-all duration-150 py-2 before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:rounded-r-full before:transition-all",
                      link.indent ? "pl-7 pr-3" : "px-3",
                      isActive
                        ? "text-accent-primary bg-accent-primary/20 font-semibold before:h-5 before:w-[3px] before:bg-accent-primary before:shadow-[0_0_10px_0_var(--accent-primary)]"
                        : "text-muted hover:text-accent-primary hover:bg-app-hover before:h-0"
                    )
                  }
                >
                  {link.label}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
}
